/**
 * Seeds Column Definitions
 *
 * Column definitions with JSX cell renderers.
 * Separated from columns.ts to isolate JSX dependencies.
 *
 * Pattern matches:
 * - src/sections/asset/config/columnDefinitions.tsx
 * - src/sections/customerManagement/config/columnDefinitions.tsx
 *
 * Column widths migrated to central registry (Day 2 - 2025-10-19)
 * Location: src/config/columns/columnRegistry.ts
 *
 * Phase 4 Update (2025-10-24):
 * - Added preseed column definitions import
 * - Updated getSeedColumns to support preseed type
 * - Updated getSeedColumnsConfig to support preseed type
 *
 * Rollback instructions (if needed):
 * 1. Revert this file to previous commit
 * 2. Central registry remains (no deletion needed)
 */

import { NameColumn } from '@/components/NameColumn';
import { OverflowText } from '@/components/OverflowText';
import { getColumnWidth } from '@/components/table/columns';
import { EmailCell } from '@/components/table/columns/cells';
import { ColumnPriority, Columns } from '@/components/table/types';
import { SeedStatusComponent } from '@/sections/seeds/components/badges';
import { TagsCell } from '@/sections/seeds/components/cells/TagsCell';
import { OriginCell } from '@/sections/seeds/components/OriginCell';
import { Seed } from '@/types';

import { SeedType } from './columns';
import {
  getPreseedColumns,
  getPreseedColumnsConfig,
} from './preseed/columnDefinitions';

// ============================================================================
// COLUMN DEFINITIONS
// ============================================================================

/**
 * Get web application seed columns configuration
 */
const getWebappSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      info: 'Status of the seed',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Asset',
      id: 'identifier',
      copy: true,
      info: 'The web application URL and group information',
      cell: (seed: Seed) => {
        return (
          <div className="flex flex-col">
            <OverflowText
              text={seed.identifier}
              className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary font-bold"
            />
            {seed.group && seed.group !== seed.identifier && (
              <span className="text-sm">{seed.group}</span>
            )}
          </div>
        );
      },
      ...getColumnWidth('seed', 'identifier'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      info: 'Custom labels for organizing and classifying seeds',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      info: 'Source of the seed (Chariot capabilities, integrations, or manual imports)',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was first discovered',
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was last observed',
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get domain seed columns configuration
 */
const getDomainSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      info: 'Status of the seed',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
      responsive: {
        priority: ColumnPriority.CRITICAL,
        alwaysVisible: true,
      },
    },
    {
      label: 'Domain/FQDN',
      id: 'identifier',
      copy: true,
      info: 'The domain or fully qualified domain name that is being scanned',
      cell: (seed: Seed) => {
        return (
          <OverflowText
            text={seed.identifier}
            className="text-chariot-brand-primary group-[.is-selected]:text-chariot-text-primary font-bold"
          />
        );
      },
      ...getColumnWidth('seed', 'identifier'),
    },
    {
      label: 'Registrar',
      id: 'registrar',
      info: 'The company that manages the domain name registration',
      cell: item =>
        item.registrar ? <OverflowText text={item.registrar} /> : '-',
      ...getColumnWidth('seed', 'registrar'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Registrant Email',
      id: 'email',
      info: 'The email address of the person or entity that registered the domain',
      cell: item => <EmailCell entity={item} field="email" />,
      ...getColumnWidth('seed', 'email'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Registrant Organization',
      id: 'registrant',
      info: 'The company or organization that owns the registered domain',
      cell: item =>
        item.registrant ? <OverflowText text={item.registrant} /> : '-',
      ...getColumnWidth('seed', 'registrant'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      info: 'Custom labels for organizing and classifying seeds',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      info: 'Source of the seed (Chariot capabilities, integrations, or manual imports)',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was first discovered',
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was last observed',
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get IP/CIDR seed columns configuration
 */
const getIpSeedColumns = (): Columns<Seed> => {
  return [
    {
      label: 'Status',
      id: 'status',
      className: 'capitalize',
      info: 'Status of the seed.',
      cell: (item: Seed) => {
        return <SeedStatusComponent seed={item} />;
      },
      ...getColumnWidth('seed', 'status'),
    },
    {
      label: 'CIDR Range/IP Address',
      copy: true,
      id: 'identifier',
      info: 'Seed is the domain or IP address that is being scanned.',
      cell: (seed: Seed) => <NameColumn name={seed.identifier} />,
      ...getColumnWidth('seed', 'identifier'),
    },
    {
      label: 'Registrant Organization',
      id: 'registrant',
      info: 'Organization that owns the IP address.',
      cell: item =>
        item.registrant ? <OverflowText text={item.registrant} /> : '-',
      ...getColumnWidth('seed', 'registrant'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Internet Service Provider',
      id: 'registrar',
      info: 'ISP that owns the IP address.',
      cell: item =>
        item.registrar ? <OverflowText text={item.registrar} /> : '-',
      ...getColumnWidth('seed', 'registrar'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Country',
      id: 'country',
      info: 'Location of the seed.',
      cell: (item: Seed) =>
        item.country ? <OverflowText text={item.country} /> : '-',
      ...getColumnWidth('seed', 'country'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Tags',
      id: 'tags',
      info: 'Custom labels for organizing and classifying seeds',
      cell: (seed: Seed) => <TagsCell seed={seed} />,
      ...getColumnWidth('seed', 'tags'),
      responsive: {
        priority: ColumnPriority.LOW,
      },
    },
    {
      label: 'Origin',
      id: 'origin',
      info: 'Source of the seed (Chariot capabilities, integrations, or manual imports)',
      cell: (item: Seed) => <OriginCell seed={item} />,
      ...getColumnWidth('seed', 'origin'),
      responsive: {
        priority: ColumnPriority.HIGH,
      },
    },
    {
      label: 'First Seen',
      id: 'created',
      ...getColumnWidth('seed', 'created'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was first discovered',
      responsive: {
        priority: ColumnPriority.MEDIUM,
        minViewportWidth: 1024,
      },
    },
    {
      label: 'Last Seen',
      id: 'visited',
      ...getColumnWidth('seed', 'visited'),
      copy: true,
      cell: 'date',
      sorting: true,
      info: 'Date seed was last observed',
      responsive: {
        priority: ColumnPriority.HIGH,
        minViewportWidth: 900,
      },
    },
  ];
};

/**
 * Get type-specific seed columns
 *
 * Updated in Phase 4 to support preseed type.
 *
 * @param type - Seed type (domain, ip, webapplication, preseed)
 * @returns Column configuration for the seed type
 *
 * @example
 * ```tsx
 * const columns = getSeedColumns('domain');
 * const preseedColumns = getSeedColumns('preseed');
 * ```
 */
export const getSeedColumns = (type: SeedType): Columns<Seed> => {
  switch (type) {
    case 'webapplication':
      return getWebappSeedColumns();
    case 'domain':
      return getDomainSeedColumns();
    case 'ip':
      return getIpSeedColumns();
    case 'preseed':
      return getPreseedColumns() as unknown as Columns<Seed>;
    default:
      // Fallback for unknown types (should never happen with proper typing)
      return getIpSeedColumns();
  }
};

/**
 * Get sortable column configuration for seed type
 *
 * Updated in Phase 4 to support preseed type with its own configuration.
 *
 * @param type - Seed type (domain, ip, webapplication, preseed)
 * @returns Sortable column configuration with default and disabled columns
 *
 * @example
 * ```tsx
 * const config = getSeedColumnsConfig('domain');
 * const preseedConfig = getSeedColumnsConfig('preseed');
 * ```
 */
export const getSeedColumnsConfig = (type: SeedType) => {
  // Preseed has its own column configuration
  if (type === 'preseed') {
    return getPreseedColumnsConfig();
  }

  const columns = getSeedColumns(type);

  // Separate email, registrar, and tags columns as available, not active by default
  const emailColumn = columns.find(col => col.id === 'email');
  const registrarColumn = columns.find(col => col.id === 'registrar');
  const tagsColumn = columns.find(col => col.id === 'tags');

  // Active columns include all columns except email, registrar, and tags
  // Timestamp columns (created, visited) are regular columns that can be hidden/reordered
  const activeColumns = columns.filter(
    col => col.id !== 'email' && col.id !== 'registrar' && col.id !== 'tags'
  );

  const availableColumns = [];
  if (emailColumn) {
    availableColumns.push({
      id: emailColumn.id,
      label: emailColumn.label,
      disabled: false,
      isDefault: false,
    });
  }
  if (registrarColumn) {
    availableColumns.push({
      id: registrarColumn.id,
      label: registrarColumn.label,
      disabled: false,
      isDefault: false,
    });
  }
  if (tagsColumn) {
    availableColumns.push({
      id: tagsColumn.id,
      label: tagsColumn.label,
      disabled: false,
      isDefault: false,
    });
  }

  return {
    fixed: [],
    active: activeColumns.map(({ id, label }) => ({
      id,
      label,
      disabled: id === 'identifier', // Only identifier is required
      isDefault: id === 'identifier',
    })),
    available: availableColumns,
  };
};
